// Terms of Service for TaskPogo. Drafted by the engineering team based
// on how the service operates today; should be reviewed by counsel
// before being relied upon in a dispute.
//
// "TaskPogo" is left spelled out rather than routed through
// Brand.PRODUCT_NAME: this page DEFINES it as a legal term above, and a
// defined term reads oddly as an interpolation.

import { Link } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../fleet-shared/Brand.mjs';
import * as Urls from '../fleet-shared/Urls.mjs';

const EFFECTIVE_DATE = 'June 3, 2026';
// The address is Brand.mjs's; aliased so the page's existing
// {CONTACT_EMAIL} references read unchanged.
const CONTACT_EMAIL = SUPPORT_EMAIL;

const Terms = () => {
  return (
    <div className="container py-4" style={{ maxWidth: 820 }}>
      <h1>Terms of Service</h1>
      <p className="text-body-secondary">Effective date: {EFFECTIVE_DATE}</p>

      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of
        the TaskPogo website and service at{' '}
        <a href="https://www.taskpogo.com">www.taskpogo.com</a>{' '}
        (&ldquo;TaskPogo&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By
        creating an account or using TaskPogo, you agree to these Terms.
        If you do not agree, do not use the service.
      </p>

      <h2>The service</h2>
      <p>
        TaskPogo is a trip-planning, marine-weather and maintenance-tracking
        tool that lets you record items, trips and routes, tasks, counters, and
        logbook entries; upload related files; view weather, tide and current
        information drawn from public government sources; and send periodic
        email digests and trip weather briefings to addresses you authorize. We
        may change, add, or remove features over time.
      </p>

      <h2>Your account</h2>
      <ul>
        <li>You must provide an accurate email address and keep your password confidential.</li>
        <li>
          You are responsible for everything that happens under your
          account, including any addresses you subscribe to email
          digests.
        </li>
        <li>
          You must be at least 13 years old (or the minimum age in your
          country) to create an account.
        </li>
        <li>
          Notify us promptly at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you
          believe your account has been used without your permission.
        </li>
      </ul>

      <h2>Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use TaskPogo to do anything illegal, infringing, harassing,
          deceptive, or harmful.
        </li>
        <li>
          Upload malware, attempt to break TaskPogo&apos;s security, or
          probe, scan, or test the vulnerability of any system or
          network without authorization.
        </li>
        <li>
          Subscribe email addresses to digests without the recipient&apos;s
          consent, or send unsolicited messages through TaskPogo.
        </li>
        <li>
          Resell or sublicense the service, or use it to build a
          competing product.
        </li>
        <li>
          Use TaskPogo to store information you are legally required to
          protect under specialized regimes (for example, regulated
          health, payment-card, or government data) unless we&apos;ve
          agreed in writing.
        </li>
      </ul>
      <p>We may suspend or terminate accounts that violate these rules.</p>

      <h2>Your content</h2>
      <p>
        You retain ownership of the content you put into TaskPogo (items,
        tasks, counters, logbook entries, files, etc.). You grant us a
        limited license to host, store, transmit, render, and back up
        that content solely to operate the service for you. We do not
        sell your content and we do not use it to train machine-learning
        models. See our{' '}
        <Link to={Urls.urlForPrivacy()}>Privacy Policy</Link> for more
        on how we handle data.
      </p>

      <h2>No warranty - maintenance reminders are not a substitute for your own diligence</h2>
      <p>
        TaskPogo is provided <strong>&ldquo;as is&rdquo;</strong> and{' '}
        <strong>&ldquo;as available&rdquo;</strong>, without warranties
        of any kind, whether express or implied, including without
        limitation warranties of merchantability, fitness for a
        particular purpose, non-infringement, accuracy, or
        uninterrupted operation.
      </p>
      <p>
        <strong>
          You are solely responsible for the maintenance, safety, and
          legal compliance of the items you track.
        </strong>{' '}
        TaskPogo&apos;s reminders, due-date and counter-based scheduling,
        and email digests are conveniences to help you remember things -
        not a guaranteed alarm system. Email delivery can be delayed or
        fail. Reminders can be miscalculated if you enter incorrect
        intervals, counter values, or completion data. <em>Always</em>{' '}
        follow the manufacturer&apos;s instructions, regulatory
        requirements, and your own judgement for anything where missing
        a task could cause harm.
      </p>
      <p>
        <strong>
          Weather, tide and current information is for planning only and is not
          a substitute for official forecasts, charts, or notices to mariners.
          Do not rely on it for navigation.
        </strong>{' '}
        Forecasts, observations, warnings, and tide and current predictions are
        drawn from third-party government sources including the US National
        Weather Service, Environment and Climate Change Canada, NOAA, and the
        Canadian Hydrographic Service. We do not produce this data, cannot
        guarantee that it is current, complete, or correct, and it may be
        delayed or unavailable. Route distances shown are straight-line
        measurements between the points you place; they are not a routed track
        and take no account of hazards, depth, traffic separation, or
        restricted areas. Always consult the official product and exercise your
        own seamanship or airmanship.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, TaskPogo and its
        operators will not be liable for any indirect, incidental,
        special, consequential, or punitive damages, or for any loss of
        profits, revenue, data, use, goodwill, or other intangible
        losses, arising out of or related to your use of (or inability
        to use) the service - including any failure of the service to
        deliver a reminder, digest, or notification on time. Our total
        cumulative liability for any claim arising out of or related to
        the service will not exceed the amounts you have paid us for the
        service in the twelve months preceding the claim, or USD $50,
        whichever is greater.
      </p>

      <h2>Termination</h2>
      <p>
        You can stop using the service at any time and request account
        deletion by emailing{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We may
        suspend or terminate your access if you violate these Terms, if
        we are required to by law, or if we discontinue the service.
        Sections that by their nature should survive termination
        (ownership, disclaimers, limitations of liability, governing
        law) will survive.
      </p>

      <h2>Changes</h2>
      <p>
        We may update these Terms as the service evolves. When we make
        material changes, we will update the effective date above and,
        where appropriate, notify you by email. Your continued use of
        TaskPogo after a change takes effect constitutes acceptance of
        the updated Terms.
      </p>

      <h2>Governing law</h2>
      <p>
        These Terms are governed by the laws of the State of Washington,
        United States, without regard to its conflict-of-laws rules. You
        and TaskPogo agree that the exclusive venue for any dispute
        arising out of or relating to these Terms or the service will be
        the state or federal courts located in King County, Washington,
        and you consent to the personal jurisdiction of those courts.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about these Terms? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </div>
  );
};

export default Terms;
